import React, { useState, useRef, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Animated, ActivityIndicator, Platform, Modal, Pressable } from 'react-native';
import Share from 'react-native-share';
import { Asset } from 'expo-asset';
import * as FileSystem from 'expo-file-system/legacy';
import { Alert } from '../../utils/toast';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Image } from 'expo-image';
import { useTheme } from '../../theme/ThemeContext';
import { ClubsStackParamList } from '../../types/navigation';
import ThemedText from '../../components/common/ThemedText';
import ClubTabBar from '../../components/clubs/ClubTabBar';
import ClubFeedTab from '../../components/clubs/ClubFeedTab';
import ClubReferralsTab from '../../components/clubs/ClubReferralsTab';
import ClubMeetupsTab from '../../components/clubs/ClubMeetupsTab';
import ClubMembersTab from '../../components/clubs/ClubMembersTab';
import ClubDetailsTab from '../../components/clubs/ClubDetailsTab';
import FloatingActionButton from '../../components/common/FloatingActionButton';
import { getClubById as fetchClubById, Club, joinClub, deleteClub } from '../../lib/api/clubs';
import { useAuth } from '../../context/AuthContext';
import { showSuccessToast, showErrorToast } from '../../utils/toast';

type ClubDetailNavigationProp = StackNavigationProp<ClubsStackParamList, 'ClubDetail'>;
type ClubDetailRouteProp = RouteProp<ClubsStackParamList, 'ClubDetail'>;

export type ClubTab = 'feed' | 'meetups' | 'members' | 'details' | 'referrals';

const ClubDetailScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<ClubDetailNavigationProp>();
  const route = useRoute<ClubDetailRouteProp>();
  const { user } = useAuth();
  const { clubId, initialTab } = route.params;
  const [activeTab, setActiveTab] = useState<ClubTab>(initialTab || 'referrals');
  const [club, setClub] = useState<Club | null>(null);
  const [loading, setLoading] = useState(true);
  const [menuVisible, setMenuVisible] = useState(false);
  const scrollY = useRef(new Animated.Value(0)).current;
  const HEADER_HEIGHT = 180; // Stats section height

  // Update active tab when initialTab param changes
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  useFocusEffect(
    React.useCallback(() => {
      loadClub();
    }, [clubId])
  );

  const loadClub = async () => {
    try {
      setLoading(true);
      const data = await fetchClubById(clubId);
      console.log('📋 Club loaded:', { 
        id: data.id, 
        name: data.name, 
        code: (data as any).code,
        isAdmin: data.isAdmin,
        isCaptain: (data as any).isCaptain,
        hasCode: !!(data as any).code,
        isPaid: data.isPaid,
        annualFee: data.annualFee,
        membershipPaymentStatus: data.membershipPaymentStatus,
      });
      setClub(data);
    } catch (error) {
      console.error('Error loading club:', error);
      Alert.alert('Error', 'Failed to load club');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </SafeAreaView>
    );
  }

  if (!club) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={48} color={theme.textTertiary} />
          <ThemedText style={[styles.errorText, { color: theme.textTertiary }]}>Club not found</ThemedText>
        </View>
      </SafeAreaView>
    );
  }

  const membershipStatus = club.membershipStatus || 'not-member';
  const isAdmin = club.isAdmin || user?.role === 'admin';
  const isCaptain = (club as any).isCaptain || false;
  const canManageCode = isAdmin || isCaptain; // Both admin and captains can see/edit code

  const handleBack = () => {
    console.log('🔍 ClubDetail handleBack called');
    console.log('🔍 Can go back:', navigation.canGoBack());
    
    if (navigation.canGoBack()) {
      console.log('🔍 Going back...');
      navigation.goBack();
    } else {
      // If we can't go back, navigate to ClubsHome
      console.log('🔍 Cannot go back, navigating to ClubsHome');
      navigation.navigate('ClubsHome');
    }
  };

  const handleJoinClub = async () => {
    // If admin, join directly without form
    if (user?.role === 'admin') {
      try {
        await joinClub(clubId, {
          phone: user?.phoneNumber || '',
          profession: user?.profession || '',
        });
        showSuccessToast('You have joined the club!');
        loadClub(); // Reload to update status
      } catch (error: any) {
        showErrorToast(error.message || 'Failed to join club');
      }
      return;
    }

    // For non-admins, navigate to join form
    navigation.navigate('JoinClubForm', { 
      clubId, 
      clubName: club?.name || 'Club' 
    });
  };



  const handleCreatePost = () => {
    navigation.navigate('CreatePost', { clubId });
  };

  const handleCreateMeetup = () => {
    navigation.navigate('SendMeetup', { clubId });
  };

  const handleCreateReferral = () => {
    navigation.navigate('CreateReferral', { clubId });
  };

  const handleDeleteClub = () => {
    setMenuVisible(false);
    Alert.alert(
      'Delete Club',
      `Are you sure you want to delete "${club?.name}"? This action cannot be undone and will permanently remove all club data, members, posts, and meetups.`,
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteClub(clubId);
              showSuccessToast('Club deleted successfully');
              navigation.navigate('ClubsHome');
            } catch (error: any) {
              showErrorToast(error.message || 'Failed to delete club');
            }
          },
        },
      ]
    );
  };

  const handleShareClub = async () => {
    setMenuVisible(false);
    try {
      console.log('🟢 [ClubDetail] Starting share...');
      console.log('🟢 Club ID:', clubId);
      console.log('🟢 Cover photo:', club?.coverPhotoUrl);
      console.log('🟢 Platform:', Platform.OS);

      const clubLink = `https://lnbapp.ln.ink/club/${clubId}`;
      const shareMessage = `Join ${club?.name} on Leads n Business 🚀\n\n${club?.description || ''}\n\n${clubLink}\n\nDownload LNB now! Your business dash board. From Leads to contracts & network, all at one place.`;

      // Try to share with image (cover photo or default)
      let imageSource: string | undefined = club?.coverPhotoUrl || undefined;
      let useDefaultImage = false;

      if (!imageSource && Platform.OS !== 'web') {
        // No cover photo — use default post.png
        console.log('🟢 No cover photo, using default post.png');
        useDefaultImage = true;
        try {
          const asset = Asset.fromModule(require('../../../assets/post.png'));
          await asset.downloadAsync();
          imageSource = asset.localUri || asset.uri;
          console.log('🟢 Default image URI:', imageSource);
        } catch (assetError) {
          console.error('❌ Error loading default image:', assetError);
          imageSource = undefined;
        }
      }

      if (imageSource && Platform.OS !== 'web') {
        try {
          let fileUriToShare: string;

          if (useDefaultImage) {
            // Local asset — copy to cache
            const fileUri = `${FileSystem.cacheDirectory}club_default.png`;
            await FileSystem.copyAsync({ from: imageSource, to: fileUri });
            console.log('🟢 Default image copied to:', fileUri);
            fileUriToShare = fileUri;
          } else {
            // Remote cover photo — download to cache
            const fileUri = `${FileSystem.cacheDirectory}club_${clubId}.jpg`;
            console.log('🟢 Downloading cover photo to:', fileUri);
            const downloadResult = await FileSystem.downloadAsync(imageSource, fileUri);
            if (downloadResult.status !== 200) {
              throw new Error('Download failed');
            }
            console.log('✅ Cover photo downloaded successfully!');
            fileUriToShare = downloadResult.uri;
          }

          await Share.open({
            title: `Join ${club?.name}`,
            message: shareMessage,
            url: Platform.OS === 'android' ? `file://${fileUriToShare}` : fileUriToShare,
            type: useDefaultImage ? 'image/png' : 'image/jpeg',
          });
          console.log('✅ Club shared successfully with image');
          return;
        } catch (imageError: any) {
          if (imageError.message !== 'User did not share') {
            console.error('❌ Error sharing club with image:', imageError);
          } else {
            return; // User cancelled
          }
        }
      } else {
        console.log('⚠️ Skipping image for web platform');
      }

      // Fallback: share text + URL only
      console.log('🟢 Falling back to message-only share');
      await Share.open({ message: shareMessage });
      console.log('✅ Club shared (message only)');
    } catch (error: any) {
      if (error.message !== 'User did not share') {
        console.error('Error sharing club:', error);
      }
    }
  };

  // Check if posting is allowed today
  const isPostingAllowedToday = () => {
    const allowedPostingDays = (club as any)?.allowedPostingDays || [];
    if (allowedPostingDays.length === 0) return true;
    const currentDay = new Date().getDay();
    return allowedPostingDays.includes(currentDay);
  };

  // Determine if FAB should be shown
  const shouldShowFAB = (membershipStatus === 'joined' || isAdmin) && (activeTab === 'feed' || activeTab === 'meetups' || activeTab === 'referrals');
  const isFeedTab = activeTab === 'feed';
  const isReferralsTab = activeTab === 'referrals';
  const isMeetupsTab = activeTab === 'meetups';
  const fabAction = isFeedTab ? handleCreatePost : isReferralsTab ? handleCreateReferral : handleCreateMeetup;
  const showFeedFAB = isFeedTab && isPostingAllowedToday();
  const showReferralFAB = isReferralsTab && isPostingAllowedToday();
  const showMeetupFAB = isMeetupsTab; // Meetups not restricted by posting days

  const renderTabContent = () => {
    switch (activeTab) {
      case 'referrals':
        return (
          <ClubReferralsTab 
            clubId={clubId} 
            allowedPostingDays={(club as any)?.allowedPostingDays || []}
            onCreateReferral={handleCreateReferral} 
          />
        );
      case 'feed':
        return (
          <ClubFeedTab 
            clubId={clubId} 
            isAdmin={isAdmin}
            allowedPostingDays={(club as any).allowedPostingDays || []}
            onCreatePost={handleCreatePost}
          />
        );
      case 'meetups':
        return <ClubMeetupsTab clubId={clubId} onCreateMeetup={handleCreateMeetup} />;
      case 'members':
        return <ClubMembersTab clubId={clubId} isAdmin={isAdmin} />;
      case 'details':
        return (
          <ClubDetailsTab
            clubId={clubId}
            isAdmin={isAdmin}
            isCaptain={isCaptain}
            membershipStatus={membershipStatus}
            initialChatEnabled={club.chatEnabled}
            initialDescription={club.description}
            initialRules={club.rules || 'No specific rules defined yet.'}
            initialAllowedPostingDays={(club as any).allowedPostingDays || []}
            initialCode={(club as any).code}
            isPaid={club.isPaid}
            annualFee={club.annualFee}
            onSettingsUpdated={loadClub}
          />
        );
      default:
        return null;
    }
  };

  const renderPublicView = () => (
    <View style={[styles.publicContainer, { backgroundColor: theme.background }]}>
      <View style={[styles.lockOverlay, { backgroundColor: theme.background + 'E0' }]}>
        <Ionicons name="lock-closed" size={48} color={theme.textTertiary} />
        <ThemedText style={[styles.lockText, { color: theme.textSecondary }]}>
          Join this club to access content
        </ThemedText>
        <TouchableOpacity
          style={[styles.joinButton, { backgroundColor: theme.accent }]}
          onPress={handleJoinClub}
          activeOpacity={0.7}
        >
          <ThemedText style={[styles.joinButtonText, { color: theme.textInverse }]}>
            Join Club
          </ThemedText>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderPendingView = () => (
    <View style={[styles.publicContainer, { backgroundColor: theme.background }]}>
      <View style={[styles.lockOverlay, { backgroundColor: theme.background + 'E0' }]}>
        <Ionicons name="time-outline" size={48} color="#F59E0B" />
        <ThemedText style={[styles.lockText, { color: theme.textSecondary }]}>
          Your join request is pending approval
        </ThemedText>
      </View>
    </View>
  );

  const renderApprovedView = () => {
    const needsPayment = club.isPaid && club.membershipPaymentStatus !== 'paid';
    return (
      <View style={[styles.publicContainer, { backgroundColor: theme.background }]}>
        <View style={[styles.lockOverlay, { backgroundColor: theme.background + 'E0' }]}>
          <Ionicons name="checkmark-circle-outline" size={48} color="#10B981" />
          <ThemedText style={[styles.lockText, { color: theme.textSecondary }]}>
            Your request has been approved!
          </ThemedText>
          {needsPayment ? (
            <>
              <ThemedText style={[styles.sublockText, { color: theme.textTertiary }]}>
                Complete your annual payment to unlock access, or enter a code shared by the admin
              </ThemedText>
              <TouchableOpacity
                style={[styles.joinButton, { backgroundColor: theme.primary, marginTop: 16 }]}
                onPress={() =>
                  navigation.navigate('ClubPayment', {
                    clubId,
                    clubName: club.name,
                    amount: club.annualFee ?? 0,
                  })
                }
                activeOpacity={0.7}
              >
                <Ionicons name="card-outline" size={20} color="#000000" style={{ marginRight: 8 }} />
                <ThemedText style={[styles.joinButtonText, { color: '#000000' }]}>
                  Pay ₹{(club.annualFee ?? 0).toLocaleString('en-IN')}/yr &amp; Join
                </ThemedText>
              </TouchableOpacity>
              <View style={styles.orDividerContainer}>
                <View style={[styles.orDividerLine, { backgroundColor: theme.border }]} />
                <ThemedText style={[styles.orDividerText, { color: theme.textTertiary }]}>OR</ThemedText>
                <View style={[styles.orDividerLine, { backgroundColor: theme.border }]} />
              </View>
              <TouchableOpacity
                style={[styles.joinButton, { backgroundColor: theme.accent }]}
                onPress={() => navigation.navigate('EnterClubCode', { clubId, clubName: club.name })}
                activeOpacity={0.7}
              >
                <Ionicons name="key" size={20} color="#FFFFFF" style={{ marginRight: 8 }} />
                <ThemedText style={[styles.joinButtonText, { color: theme.textInverse }]}>
                  Enter code to join free
                </ThemedText>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <ThemedText style={[styles.sublockText, { color: theme.textTertiary }]}>
                Enter the code shared by admin to complete joining
              </ThemedText>
              <TouchableOpacity
                style={[styles.joinButton, { backgroundColor: theme.accent, marginTop: 16 }]}
                onPress={() => navigation.navigate('EnterClubCode', { clubId, clubName: club.name })}
                activeOpacity={0.7}
              >
                <Ionicons name="key" size={20} color="#FFFFFF" style={{ marginRight: 8 }} />
                <ThemedText style={[styles.joinButtonText, { color: theme.textInverse }]}>
                  Enter Club Code
                </ThemedText>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    );
  };

  // Define menu options for admins
  const adminMenuOptions = [
    {
      icon: 'chatbubbles-outline' as const,
      label: 'Chat',
      color: theme.accent,
      onPress: () => {
        setMenuVisible(false);
        navigation.navigate('ClubChat', { clubId: club.id });
      },
    },
    {
      icon: 'share-outline' as const,
      label: 'Share',
      color: theme.text,
      onPress: handleShareClub,
    },
    {
      icon: 'create-outline' as const,
      label: 'Edit',
      color: theme.text,
      onPress: () => {
        setMenuVisible(false);
        navigation.navigate('EditClub', { clubId: club.id });
      },
    },
    {
      icon: 'trash-outline' as const,
      label: 'Delete',
      color: theme.error,
      onPress: handleDeleteClub,
    },
  ];

  const renderActionMenu = () => (
    <Modal
      visible={menuVisible}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setMenuVisible(false)}
    >
      <Pressable 
        style={styles.modalOverlay} 
        onPress={() => setMenuVisible(false)}
      >
        <View style={[styles.menuContainer, { backgroundColor: theme.card, borderColor: theme.border }]}>
          {adminMenuOptions.map((option, index) => (
            <TouchableOpacity
              key={option.label}
              style={[
                styles.menuItem,
                index < adminMenuOptions.length - 1 && { borderBottomWidth: 1, borderBottomColor: theme.border },
              ]}
              onPress={option.onPress}
              activeOpacity={0.7}
            >
              <Ionicons name={option.icon} size={22} color={option.color} />
              <ThemedText style={[styles.menuItemText, { color: option.color }]}>
                {option.label}
              </ThemedText>
            </TouchableOpacity>
          ))}
        </View>
      </Pressable>
    </Modal>
  );

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      {renderActionMenu()}
      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.card, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={handleBack} style={styles.headerButton} activeOpacity={0.7}>
          <Ionicons name="arrow-back" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>{club.name}</ThemedText>
        {isAdmin ? (
          <View style={styles.headerActions}>
            <TouchableOpacity 
              onPress={() => setMenuVisible(true)} 
              style={styles.headerActionButton} 
              activeOpacity={0.7}
            >
              <Ionicons name="ellipsis-vertical" size={24} color={theme.text} />
            </TouchableOpacity>
          </View>
        ) : membershipStatus === 'joined' ? (
          <View style={styles.headerActions}>
            <TouchableOpacity 
              onPress={handleShareClub} 
              style={styles.headerActionButton} 
              activeOpacity={0.7}
            >
              <Ionicons name="share-outline" size={24} color={theme.text} />
            </TouchableOpacity>
            <TouchableOpacity 
              onPress={() => navigation.navigate('ClubChat', { clubId: club.id })} 
              style={styles.headerActionButton} 
              activeOpacity={0.7}
            >
              <Ionicons name="chatbubbles-outline" size={24} color={theme.accent} />
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.headerButton} />
        )}
      </View>

      <Animated.ScrollView
        style={styles.scrollView}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false }
        )}
        scrollEventThrottle={16}
        stickyHeaderIndices={[1]}
      >
        {/* Club Stats - Prominent Display */}
        <View style={[styles.statsSection, { backgroundColor: theme.card, borderBottomColor: theme.border }]}>
          <View style={styles.statsGrid}>
            <View style={styles.statBox}>
              <View style={[styles.statIconContainer, { backgroundColor: theme.primary + '20' }]}>
                <Ionicons name="people" size={18} color={theme.primary} />
              </View>
              <ThemedText style={[styles.statValue, { color: theme.text }]}>{club.memberCount}</ThemedText>
              <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>Members</ThemedText>
            </View>
            
            {club.meetupCount !== undefined && (
              <>
                <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
                <View style={styles.statBox}>
                  <View style={[styles.statIconContainer, { backgroundColor: theme.accent + '20' }]}>
                    <Ionicons name="calendar" size={18} color={theme.accent} />
                  </View>
                  <ThemedText style={[styles.statValue, { color: theme.text }]}>{club.meetupCount}</ThemedText>
                  <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>Meetups</ThemedText>
                </View>
              </>
            )}
            
            {club.referralCount !== undefined && (
              <>
                <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
                <View style={styles.statBox}>
                  <View style={[styles.statIconContainer, { backgroundColor: '#10B981' + '20' }]}>
                    <Ionicons name="megaphone" size={18} color="#10B981" />
                  </View>
                  <ThemedText style={[styles.statValue, { color: theme.text }]}>{club.referralCount}</ThemedText>
                  <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>Bintros</ThemedText>
                </View>
              </>
            )}
          </View>
          
          <View style={[styles.clubMetaInfo, { borderTopColor: theme.border }]}>
            <View style={styles.metaItem}>
              <Ionicons name="pricetag" size={16} color={theme.textSecondary} />
              <ThemedText style={[styles.metaText, { color: theme.textSecondary }]}>{club.category}</ThemedText>
            </View>
            <View style={styles.metaItem}>
              <Ionicons name="location" size={16} color={theme.textSecondary} />
              <ThemedText style={[styles.metaText, { color: theme.textSecondary }]}>{club.city}</ThemedText>
            </View>
          </View>
        </View>

        {/* Sticky Tabs Header */}
        <View style={[styles.stickyTabsContainer, { backgroundColor: theme.background }]}>
          {membershipStatus === 'not-member' && !isAdmin ? (
            <View style={[styles.detailsOnlyTab, { backgroundColor: theme.background, borderBottomColor: theme.border }]}>
              <View style={[styles.tab, { borderBottomColor: theme.accent, borderBottomWidth: 2 }]}>
                <Ionicons name="information-circle-outline" size={20} color={theme.accent} />
                <ThemedText style={[styles.tabLabel, { color: theme.accent }]}>Details</ThemedText>
              </View>
            </View>
          ) : membershipStatus === 'pending' && !isAdmin ? (
            <View style={[styles.detailsOnlyTab, { backgroundColor: theme.background, borderBottomColor: theme.border }]}>
              <View style={[styles.tab, { borderBottomColor: '#F59E0B', borderBottomWidth: 2 }]}>
                <Ionicons name="time-outline" size={20} color="#F59E0B" />
                <ThemedText style={[styles.tabLabel, { color: '#F59E0B' }]}>Pending Approval</ThemedText>
              </View>
            </View>
          ) : membershipStatus === 'approved' && !isAdmin ? (
            <View style={[styles.detailsOnlyTab, { backgroundColor: theme.background, borderBottomColor: theme.border }]}>
              <View style={[styles.tab, { borderBottomColor: '#10B981', borderBottomWidth: 2 }]}>
                <Ionicons
                  name={club.isPaid && club.membershipPaymentStatus !== 'paid' ? 'card-outline' : 'key'}
                  size={20}
                  color="#10B981"
                />
                <ThemedText style={[styles.tabLabel, { color: '#10B981' }]}>
                  {club.isPaid && club.membershipPaymentStatus !== 'paid' ? 'Approved - Pay to Join' : 'Approved - Enter Code'}
                </ThemedText>
              </View>
            </View>
          ) : (
            <ClubTabBar activeTab={activeTab} onTabChange={setActiveTab} />
          )}
        </View>

        {/* Tab Content */}
        <View style={styles.tabContentContainer}>
          {membershipStatus === 'not-member' && !canManageCode && (
            <View>
              <ClubDetailsTab
                clubId={clubId}
                isAdmin={false}
                isCaptain={false}
                initialChatEnabled={club.chatEnabled}
                initialDescription={club.description}
                initialRules={club.rules || 'No specific rules defined yet.'}
                initialAllowedPostingDays={(club as any).allowedPostingDays || []}
                initialCode={(club as any).code}
                isPaid={club.isPaid}
                annualFee={club.annualFee}
                onSettingsUpdated={loadClub}
              />
              {/* Join Button at the bottom */}
              <View style={[styles.joinSection, { backgroundColor: theme.card }]}>
                {club.isPaid && (
                  <ThemedText style={[styles.feeNotice, { color: theme.textSecondary }]}>
                    Annual fee: ₹{(club.annualFee ?? 0).toLocaleString('en-IN')} — payable after admin approval
                  </ThemedText>
                )}
                <TouchableOpacity
                  style={[styles.joinButton, { backgroundColor: theme.accent }]}
                  onPress={handleJoinClub}
                  activeOpacity={0.7}
                >
                  <Ionicons name="person-add" size={20} color={theme.textInverse} />
                  <ThemedText style={[styles.joinButtonText, { color: theme.textInverse }]}>
                    {club.isPaid ? `Join (₹${(club.annualFee ?? 0).toLocaleString('en-IN')}/yr)` : 'Join Club'}
                  </ThemedText>
                </TouchableOpacity>
              </View>
            </View>
          )}
          {membershipStatus === 'pending' && !isAdmin && renderPendingView()}
          {membershipStatus === 'approved' && !isAdmin && renderApprovedView()}
          {(membershipStatus === 'joined' || isAdmin) && renderTabContent()}
        </View>
      </Animated.ScrollView>
      
      {/* Floating Action Button - Shows for feed, referrals, and meetups tabs */}
      {shouldShowFAB && (
        isFeedTab ? showFeedFAB : 
        isReferralsTab ? showReferralFAB : 
        showMeetupFAB
      ) && (
        <FloatingActionButton onPress={fabAction} />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  headerActionButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  statsSection: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    marginBottom: 12,
  },
  statBox: {
    alignItems: 'center',
    gap: 4,
    flex: 1,
  },
  statDivider: {
    width: 1,
    height: 50,
    marginHorizontal: 8,
  },
  orDividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 12,
  },
  orDividerLine: {
    flex: 1,
    height: 1,
  },
  orDividerText: {
    marginHorizontal: 12,
    fontSize: 13,
    fontWeight: '600',
  },
  statIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 2,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
  },
  statLabel: {
    fontSize: 12,
    fontWeight: '500',
  },
  clubMetaInfo: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 20,
    paddingTop: 12,
    borderTopWidth: 1,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    fontSize: 14,
  },
  stickyTabsContainer: {
    zIndex: 100,
  },
  tabContentContainer: {
    // Content will size itself naturally
  },
  tabContent: {
    flex: 1,
  },
  detailsOnlyTab: {
    borderBottomWidth: 1,
    paddingHorizontal: 8,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    gap: 6,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  joinSection: {
    padding: 16,
    marginTop: 24,
    borderRadius: 12,
    margin: 16,
  },
  feeNotice: {
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 10,
  },
  publicContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  lockOverlay: {
    alignItems: 'center',
    gap: 16,
    padding: 32,
    borderRadius: 16,
    width: '100%',
  },
  lockText: {
    fontSize: 16,
    textAlign: 'center',
  },
  sublockText: {
    fontSize: 14,
    textAlign: 'center',
    marginTop: 4,
  },
  joinButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 8,
    marginTop: 8,
  },
  joinButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    marginTop: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    paddingTop: 60,
    paddingRight: 16,
  },
  menuContainer: {
    borderRadius: 12,
    borderWidth: 1,
    minWidth: 180,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 5,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  menuItemText: {
    fontSize: 16,
    fontWeight: '500',
  },
});

export default ClubDetailScreen;
